import { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  Pencil,
  ChevronsUpDown,
  Check,
  Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAppSetting, useUpdateAppSetting } from "@/hooks/useAppSettings";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import {
  useQuoteLineItemTemplates,
  useCreateQuoteLineItemTemplate,
  useUpdateQuoteLineItemTemplate,
  useDeleteQuoteLineItemTemplate,
  useRenameLineItemTemplateCategory,
  TemplateTranslations,
  QuoteLineItemTemplate,
} from "@/hooks/useQuoteLineItemTemplates";
import {
  useQuoteGroupTemplates,
  useCreateQuoteGroupTemplate,
  useUpdateQuoteGroupTemplate,
  useDeleteQuoteGroupTemplate,
  useRenameGroupTemplateCategory,
  QuoteGroupTemplate,
} from "@/hooks/useQuoteGroupTemplates";
import { SUPPORTED_LANGUAGES } from "@/lib/languages";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

// ── Line Item Templates Tab ──────────────────────────────────────────

function LineItemTemplatesTab() {
  const { data: templates = [], isLoading } = useQuoteLineItemTemplates();
  const createTemplate = useCreateQuoteLineItemTemplate();
  const updateTemplate = useUpdateQuoteLineItemTemplate();
  const deleteTemplate = useDeleteQuoteLineItemTemplate();
  const renameCategory = useRenameLineItemTemplateCategory();

  const [editingTemplate, setEditingTemplate] =
    useState<QuoteLineItemTemplate | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [renamingCategory, setRenamingCategory] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState("");

  const [formCode, setFormCode] = useState("");
  const [formNames, setFormNames] = useState<TemplateTranslations>({});
  const [formAmount, setFormAmount] = useState(1);
  const [formUnitPrice, setFormUnitPrice] = useState(0);
  const [formCategory, setFormCategory] = useState("");
  const [formLineItemType, setFormLineItemType] = useState<
    "generic" | "equipment" | "crew"
  >("generic");

  const resetForm = () => {
    setFormCode("");
    setFormNames({});
    setFormAmount(1);
    setFormUnitPrice(0);
    setFormCategory("");
    setFormLineItemType("generic");
    setEditingTemplate(null);
  };

  const openNewForm = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const openEditForm = (template: QuoteLineItemTemplate) => {
    setEditingTemplate(template);
    setFormCode(template.code || "");
    setFormNames({ ...template.name });
    setFormAmount(template.default_amount);
    setFormUnitPrice(template.default_unit_price);
    setFormCategory(template.category || "");
    setFormLineItemType(template.line_item_type);
    setIsFormOpen(true);
  };

  const handleSave = () => {
    const hasAtLeastOneName = Object.values(formNames).some(
      (n) => n.trim() !== "",
    );
    if (!hasAtLeastOneName) return;

    const cleanedNames: TemplateTranslations = {};
    for (const [lang, val] of Object.entries(formNames)) {
      if (val.trim()) cleanedNames[lang] = val.trim();
    }

    if (editingTemplate) {
      updateTemplate.mutate(
        {
          id: editingTemplate.id,
          updates: {
            code: formCode.trim() || null,
            name: cleanedNames,
            default_amount: formAmount,
            default_unit_price: formUnitPrice,
            line_item_type: formLineItemType,
            category: formCategory || null,
          },
        },
        { onSuccess: () => setIsFormOpen(false) },
      );
    } else {
      createTemplate.mutate(
        {
          code: formCode.trim() || null,
          name: cleanedNames,
          default_amount: formAmount,
          default_unit_price: formUnitPrice,
          line_item_type: formLineItemType,
          category: formCategory || null,
        },
        { onSuccess: () => setIsFormOpen(false) },
      );
    }
  };

  const handleDelete = () => {
    if (!deleteId) return;
    deleteTemplate.mutate(deleteId, {
      onSuccess: () => setDeleteId(null),
    });
  };

  const groupedTemplates = templates.reduce<
    Record<string, QuoteLineItemTemplate[]>
  >((acc, t) => {
    const cat = t.category || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <>
      <div className="flex justify-end">
        <Button
          offlineEdit
          onClick={openNewForm}
          data-testid="new-line-template-button"
        >
          <Plus className="h-4 w-4 mr-1" />
          New Template
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-sm">No line item templates yet</p>
          <p className="text-xs mt-1">Create a template to get started</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedTemplates)
            .sort(([a], [b]) => {
              if (a === "Uncategorized") return 1;
              if (b === "Uncategorized") return -1;
              return a.localeCompare(b);
            })
            .map(([category, items]) => (
              <div key={category}>
                <div className="flex items-center gap-1 mb-2 group/cat">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    {category}
                  </h4>
                  {category !== "Uncategorized" && (
                    <Button
                      offlineEdit
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover/cat:opacity-100"
                      onClick={() => {
                        setRenamingCategory(category);
                        setNewCategoryName(category);
                      }}
                      data-testid={`line-template-rename-category-${category}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                <div className="space-y-1">
                  {items.map((template) => (
                    <div
                      key={template.id}
                      data-testid={`line-template-card-${template.id}`}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors group"
                    >
                      <div className="min-w-0 flex-1">
                        <p
                          className="font-medium text-sm truncate"
                          data-testid={`line-template-name-${template.id}`}
                        >
                          {template.code && (
                            <span className="text-muted-foreground mr-1.5">
                              {template.code}
                            </span>
                          )}
                          {template.name.de ||
                            template.name.en ||
                            Object.values(template.name)[0]}
                        </p>
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          {SUPPORTED_LANGUAGES.filter(
                            (l) => template.name[l.value],
                          ).map((l) => (
                            <span key={l.value}>
                              {l.value.toUpperCase()}: {template.name[l.value]}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {template.line_item_type !== "generic" && (
                          <span className="capitalize bg-muted px-1.5 py-0.5 rounded">
                            {template.line_item_type}
                          </span>
                        )}
                        <span className="tabular-nums">
                          {template.default_amount} @ CHF{" "}
                          {template.default_unit_price.toFixed(2)}
                        </span>
                        <Button
                          offlineEdit
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100"
                          onClick={() => openEditForm(template)}
                          data-testid={`line-template-edit-${template.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          offlineEdit
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(template.id)}
                          data-testid={`line-template-delete-${template.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Template" : "New Template"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Code (Optional)</Label>
              <Input
                placeholder="e.g. M01, L01"
                value={formCode}
                onChange={(e) => setFormCode(e.target.value)}
                autoFocus
                data-testid="line-template-code-input"
              />
            </div>

            {SUPPORTED_LANGUAGES.map((lang) => (
              <div key={lang.value} className="space-y-1">
                <Label>Name ({lang.label})</Label>
                <Input
                  placeholder={`Name in ${lang.label}`}
                  value={formNames[lang.value] || ""}
                  onChange={(e) =>
                    setFormNames((prev) => ({
                      ...prev,
                      [lang.value]: e.target.value,
                    }))
                  }
                  data-testid={`line-template-name-input-${lang.value}`}
                />
              </div>
            ))}

            <div className="space-y-1">
              <Label>Category</Label>
              <Input
                placeholder="e.g. Personnel, Equipment, Travel"
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value)}
                data-testid="line-template-category-input"
              />
            </div>

            <div className="space-y-1">
              <Label>Type</Label>
              <Select
                value={formLineItemType}
                onValueChange={(v) =>
                  setFormLineItemType(v as "generic" | "equipment" | "crew")
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="generic">Generic</SelectItem>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="crew">Crew</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Default Amount</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.5}
                  value={formAmount}
                  onChange={(e) =>
                    setFormAmount(parseFloat(e.target.value) || 0)
                  }
                  data-testid="line-template-amount-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Default Unit Price</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={formUnitPrice}
                  onChange={(e) =>
                    setFormUnitPrice(parseFloat(e.target.value) || 0)
                  }
                  data-testid="line-template-unit-price-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>
              Cancel
            </Button>
            <Button
              offlineEdit
              onClick={handleSave}
              disabled={createTemplate.isPending || updateTemplate.isPending}
              data-testid="line-template-save-button"
            >
              {editingTemplate ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this template? This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="line-template-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!renamingCategory}
        onOpenChange={() => setRenamingCategory(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>New Name</Label>
            <Input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              data-testid="line-template-rename-category-input"
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  newCategoryName.trim() &&
                  renamingCategory
                ) {
                  renameCategory.mutate(
                    {
                      oldCategory: renamingCategory,
                      newCategory: newCategoryName.trim(),
                    },
                    { onSuccess: () => setRenamingCategory(null) },
                  );
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenamingCategory(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!renamingCategory || !newCategoryName.trim()) return;
                renameCategory.mutate(
                  {
                    oldCategory: renamingCategory,
                    newCategory: newCategoryName.trim(),
                  },
                  { onSuccess: () => setRenamingCategory(null) },
                );
              }}
              disabled={renameCategory.isPending || !newCategoryName.trim()}
              data-testid="line-template-rename-category-save"
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Line Item Template Picker ────────────────────────────────────────

function LineItemTemplatePicker({
  templates,
  value,
  onChange,
  testId,
}: {
  templates: QuoteLineItemTemplate[];
  value: string | null;
  onChange: (id: string) => void;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? templates.find((t) => t.id === value) : null;
  const selectedName = selected
    ? selected.name.de || selected.name.en || Object.values(selected.name)[0]
    : null;
  const selectedLabel =
    selected?.code && selectedName
      ? `${selected.code} ${selectedName}`
      : selectedName;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <span className="truncate">
            {selectedLabel || "Select a template..."}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          <CommandInput placeholder="Search templates..." />
          <CommandList>
            <CommandEmpty>No templates found.</CommandEmpty>
            <CommandGroup>
              {templates.map((t) => {
                const translated =
                  t.name.de || t.name.en || Object.values(t.name)[0];
                const label = t.code ? `${t.code} ${translated}` : translated;
                return (
                  <CommandItem
                    key={t.id}
                    value={label}
                    onSelect={() => {
                      onChange(t.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === t.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{label}</span>
                      <span className="text-xs text-muted-foreground">
                        {t.default_amount} @ CHF{" "}
                        {t.default_unit_price.toFixed(2)}
                      </span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── Group Templates Tab ──────────────────────────────────────────────

interface GroupTemplateItemForm {
  key: string; // client-side key for React
  line_item_template_id: string | null;
  default_amount: number;
  is_title: boolean;
  name: TemplateTranslations;
}

function GroupTemplatesTab() {
  const { data: groupTemplates = [], isLoading } = useQuoteGroupTemplates();
  const { data: lineItemTemplates = [] } = useQuoteLineItemTemplates();
  const createGroupTemplate = useCreateQuoteGroupTemplate();
  const updateGroupTemplate = useUpdateQuoteGroupTemplate();
  const deleteGroupTemplate = useDeleteQuoteGroupTemplate();
  const renameCategory = useRenameGroupTemplateCategory();

  const [editingTemplate, setEditingTemplate] =
    useState<QuoteGroupTemplate | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [renamingCategory, setRenamingCategory] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState("");

  const [formNames, setFormNames] = useState<TemplateTranslations>({});
  const [formCategory, setFormCategory] = useState("");
  const [formIsOptional, setFormIsOptional] = useState(false);
  const [formApplyProjectDiscount, setFormApplyProjectDiscount] =
    useState(true);
  const [formItems, setFormItems] = useState<GroupTemplateItemForm[]>([]);

  let itemKeyCounter = 0;
  const nextKey = () => `item-${Date.now()}-${itemKeyCounter++}`;

  const resetForm = () => {
    setFormNames({});
    setFormCategory("");
    setFormIsOptional(false);
    setFormApplyProjectDiscount(true);
    setFormItems([]);
    setEditingTemplate(null);
  };

  const openNewForm = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const openEditForm = (template: QuoteGroupTemplate) => {
    setEditingTemplate(template);
    setFormNames({ ...template.name });
    setFormCategory(template.category || "");
    setFormIsOptional(template.default_is_optional);
    setFormApplyProjectDiscount(template.default_apply_project_discount);
    setFormItems(
      template.items.map((item) => ({
        key: item.id,
        line_item_template_id: item.line_item_template_id,
        default_amount: item.default_amount,
        is_title: item.is_title,
        name: item.is_title && item.name ? { ...item.name } : {},
      })),
    );
    setIsFormOpen(true);
  };

  const handleAddItem = () => {
    setFormItems((prev) => [
      ...prev,
      {
        key: nextKey(),
        line_item_template_id: null,
        default_amount: 1,
        is_title: false,
        name: {},
      },
    ]);
  };

  const handleAddTitleItem = () => {
    setFormItems((prev) => [
      ...prev,
      {
        key: nextKey(),
        line_item_template_id: null,
        default_amount: 0,
        is_title: true,
        name: {},
      },
    ]);
  };

  const handleUpdateItem = (
    index: number,
    updates: Partial<GroupTemplateItemForm>,
  ) => {
    setFormItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ...updates } : item)),
    );
  };

  const handleRemoveItem = (index: number) => {
    setFormItems((prev) => prev.filter((_, i) => i !== index));
  };

  const getTemplateName = (id: string) => {
    const t = lineItemTemplates.find((t) => t.id === id);
    if (!t) return "Unknown";
    return t.name.de || t.name.en || Object.values(t.name)[0] || "Unnamed";
  };

  const handleSave = () => {
    const hasAtLeastOneName = Object.values(formNames).some(
      (n) => n.trim() !== "",
    );
    if (!hasAtLeastOneName) return;

    const cleanedNames: TemplateTranslations = {};
    for (const [lang, val] of Object.entries(formNames)) {
      if (val.trim()) cleanedNames[lang] = val.trim();
    }

    const cleanedItems = formItems
      .filter((item) => item.is_title || item.line_item_template_id !== null)
      .map((item, index) => {
        if (item.is_title) {
          const cleanedName: TemplateTranslations = {};
          for (const [lang, val] of Object.entries(item.name)) {
            if (val.trim()) cleanedName[lang] = val.trim();
          }
          return {
            is_title: true as const,
            name: cleanedName,
            line_item_template_id: null,
            default_amount: 0,
            sort_order: index,
          };
        }
        return {
          is_title: false as const,
          name: null,
          line_item_template_id: item.line_item_template_id!,
          default_amount: item.default_amount,
          sort_order: index,
        };
      });

    if (editingTemplate) {
      updateGroupTemplate.mutate(
        {
          id: editingTemplate.id,
          updates: {
            name: cleanedNames,
            default_is_optional: formIsOptional,
            default_apply_project_discount: formApplyProjectDiscount,
            category: formCategory || null,
          },
          items: cleanedItems,
        },
        { onSuccess: () => setIsFormOpen(false) },
      );
    } else {
      createGroupTemplate.mutate(
        {
          name: cleanedNames,
          default_is_optional: formIsOptional,
          default_apply_project_discount: formApplyProjectDiscount,
          category: formCategory || null,
          items: cleanedItems,
        },
        { onSuccess: () => setIsFormOpen(false) },
      );
    }
  };

  const handleDelete = () => {
    if (!deleteId) return;
    deleteGroupTemplate.mutate(deleteId, {
      onSuccess: () => setDeleteId(null),
    });
  };

  const grouped = groupTemplates.reduce<Record<string, QuoteGroupTemplate[]>>(
    (acc, t) => {
      const cat = t.category || "Uncategorized";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(t);
      return acc;
    },
    {},
  );

  return (
    <>
      <div className="flex justify-end">
        <Button
          offlineEdit
          onClick={openNewForm}
          data-testid="new-group-template-button"
        >
          <Plus className="h-4 w-4 mr-1" />
          New Group Template
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : groupTemplates.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-sm">No group templates yet</p>
          <p className="text-xs mt-1">Create a group template to get started</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped)
            .sort(([a], [b]) => {
              if (a === "Uncategorized") return 1;
              if (b === "Uncategorized") return -1;
              return a.localeCompare(b);
            })
            .map(([category, items]) => (
              <div key={category}>
                <div className="flex items-center gap-1 mb-2 group/cat">
                  <h4 className="text-sm font-medium text-muted-foreground">
                    {category}
                  </h4>
                  {category !== "Uncategorized" && (
                    <Button
                      offlineEdit
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover/cat:opacity-100"
                      onClick={() => {
                        setRenamingCategory(category);
                        setNewCategoryName(category);
                      }}
                      data-testid={`group-template-rename-category-${category}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                <div className="space-y-1">
                  {items.map((template) => (
                    <div
                      key={template.id}
                      data-testid={`group-template-card-${template.id}`}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors group"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p
                            className="font-medium text-sm truncate"
                            data-testid={`group-template-name-${template.id}`}
                          >
                            {template.name.de ||
                              template.name.en ||
                              Object.values(template.name)[0]}
                          </p>
                          {template.default_is_optional && (
                            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              Optional
                            </span>
                          )}
                          {!template.default_apply_project_discount && (
                            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              No discount
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          {SUPPORTED_LANGUAGES.filter(
                            (l) => template.name[l.value],
                          ).map((l) => (
                            <span key={l.value}>
                              {l.value.toUpperCase()}: {template.name[l.value]}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>
                          {template.items.length} item
                          {template.items.length !== 1 ? "s" : ""}
                        </span>
                        <Button
                          offlineEdit
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100"
                          onClick={() => openEditForm(template)}
                          data-testid={`group-template-edit-${template.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          offlineEdit
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(template.id)}
                          data-testid={`group-template-delete-${template.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Group Template" : "New Group Template"}
            </DialogTitle>
            <DialogDescription>
              Define a reusable group with pre-configured line items.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <div key={lang.value} className="space-y-1">
                <Label>Group Name ({lang.label})</Label>
                <Input
                  placeholder={`Name in ${lang.label}`}
                  value={formNames[lang.value] || ""}
                  onChange={(e) =>
                    setFormNames((prev) => ({
                      ...prev,
                      [lang.value]: e.target.value,
                    }))
                  }
                  data-testid={`group-template-name-input-${lang.value}`}
                />
              </div>
            ))}

            <div className="space-y-1">
              <Label>Category</Label>
              <Input
                placeholder="e.g. Standard, Event Types"
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value)}
                data-testid="group-template-category-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="group-template-optional"
                checked={formIsOptional}
                onCheckedChange={setFormIsOptional}
              />
              <Label htmlFor="group-template-optional" className="font-normal">
                Optional by default
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="group-template-apply-project-discount"
                checked={formApplyProjectDiscount}
                onCheckedChange={setFormApplyProjectDiscount}
                data-testid="group-template-apply-project-discount"
              />
              <Label
                htmlFor="group-template-apply-project-discount"
                className="font-normal"
              >
                Apply project discount by default
              </Label>
            </div>

            {/* Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Line Items</Label>
                <div className="flex gap-2">
                  <Button
                    offlineEdit
                    variant="outline"
                    size="sm"
                    onClick={handleAddTitleItem}
                    data-testid="group-template-add-title"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Title
                  </Button>
                  <Button
                    offlineEdit
                    variant="outline"
                    size="sm"
                    onClick={handleAddItem}
                    data-testid="group-template-add-item"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Item
                  </Button>
                </div>
              </div>

              {formItems.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  No items yet. Add items that will be created with this group.
                </p>
              ) : (
                <div className="space-y-3">
                  {formItems.map((item, index) => {
                    if (item.is_title) {
                      return (
                        <div
                          key={item.key}
                          className="rounded-lg border p-3 space-y-2 bg-muted/30"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground">
                              Title {index + 1}
                            </span>
                            <Button
                              offlineEdit
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive hover:text-destructive"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          {SUPPORTED_LANGUAGES.map((lang) => (
                            <div key={lang.value} className="space-y-1">
                              <Label className="text-xs">
                                Title ({lang.label})
                              </Label>
                              <Input
                                placeholder={`Title in ${lang.label}`}
                                value={item.name[lang.value] || ""}
                                onChange={(e) => {
                                  const newName = {
                                    ...item.name,
                                    [lang.value]: e.target.value,
                                  };
                                  handleUpdateItem(index, { name: newName });
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      );
                    }

                    const selectedTemplate = item.line_item_template_id
                      ? lineItemTemplates.find(
                          (t) => t.id === item.line_item_template_id,
                        )
                      : null;
                    return (
                      <div
                        key={item.key}
                        className="rounded-lg border p-3 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground">
                            Item {index + 1}
                          </span>
                          <Button
                            offlineEdit
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveItem(index)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Line Item Template</Label>
                          <LineItemTemplatePicker
                            templates={lineItemTemplates}
                            value={item.line_item_template_id}
                            onChange={(id) =>
                              handleUpdateItem(index, {
                                line_item_template_id: id,
                              })
                            }
                            testId={`group-template-line-item-picker-${index}`}
                          />
                        </div>
                        {selectedTemplate && (
                          <p className="text-xs text-muted-foreground capitalize">
                            Type: {selectedTemplate.line_item_type} | Unit
                            Price: CHF{" "}
                            {selectedTemplate.default_unit_price.toFixed(2)}
                          </p>
                        )}
                        <div className="space-y-1">
                          <Label className="text-xs">Amount Override</Label>
                          <Input
                            type="number"
                            min={0}
                            step={0.5}
                            value={item.default_amount}
                            onChange={(e) =>
                              handleUpdateItem(index, {
                                default_amount: parseFloat(e.target.value) || 0,
                              })
                            }
                            data-testid={`group-template-item-amount-${index}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>
              Cancel
            </Button>
            <Button
              offlineEdit
              onClick={handleSave}
              disabled={
                createGroupTemplate.isPending || updateGroupTemplate.isPending
              }
              data-testid="group-template-save-button"
            >
              {editingTemplate ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this group template and all its
              items? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="group-template-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!renamingCategory}
        onOpenChange={() => setRenamingCategory(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>New Name</Label>
            <Input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              data-testid="group-template-rename-category-input"
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  newCategoryName.trim() &&
                  renamingCategory
                ) {
                  renameCategory.mutate(
                    {
                      oldCategory: renamingCategory,
                      newCategory: newCategoryName.trim(),
                    },
                    { onSuccess: () => setRenamingCategory(null) },
                  );
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenamingCategory(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!renamingCategory || !newCategoryName.trim()) return;
                renameCategory.mutate(
                  {
                    oldCategory: renamingCategory,
                    newCategory: newCategoryName.trim(),
                  },
                  { onSuccess: () => setRenamingCategory(null) },
                );
              }}
              disabled={renameCategory.isPending || !newCategoryName.trim()}
              data-testid="group-template-rename-category-save"
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── General Conditions Templates Tab ──────────────────────────────────

function GeneralConditionsTab() {
  const { data: templates, isLoading } = useAppSetting<Record<string, string>>(
    "general_conditions_templates",
  );
  const updateSetting = useUpdateAppSetting();

  const [localTemplates, setLocalTemplates] = useState<Record<string, string>>(
    {},
  );
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (templates) {
      setLocalTemplates(templates);
      setIsDirty(false);
    }
  }, [templates]);

  const handleChange = (lang: string, value: string) => {
    setLocalTemplates((prev) => ({ ...prev, [lang]: value }));
    setIsDirty(true);
  };

  const handleSave = () => {
    updateSetting.mutate(
      { key: "general_conditions_templates", value: localTemplates },
      { onSuccess: () => setIsDirty(false) },
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Default general conditions text used when creating new quote versions.
        Each language has its own template. Per-quote conditions can still be
        edited individually.
      </p>

      {SUPPORTED_LANGUAGES.map((lang) => (
        <Card key={lang.value}>
          <CardHeader>
            <CardTitle className="text-base">{lang.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              rows={12}
              value={localTemplates[lang.value] || ""}
              onChange={(e) => handleChange(lang.value, e.target.value)}
              placeholder={`General conditions in ${lang.label}...`}
              className="font-mono text-xs"
            />
          </CardContent>
        </Card>
      ))}

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!isDirty || updateSetting.isPending}
        >
          <Save className="h-4 w-4 mr-1" />
          {updateSetting.isPending ? "Saving..." : "Save Templates"}
        </Button>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────

export default function QuoteTemplatesPage() {
  return (
    <div className="space-y-6 animate-fade-in h-full overflow-auto">
      <PageHeader title="Quote Templates" />

      <Tabs defaultValue="line-items">
        <TabsList>
          <TabsTrigger value="line-items">Line Item Templates</TabsTrigger>
          <TabsTrigger value="groups">Group Templates</TabsTrigger>
          <TabsTrigger value="conditions">General Conditions</TabsTrigger>
        </TabsList>

        <TabsContent value="line-items" className="space-y-6">
          <LineItemTemplatesTab />
        </TabsContent>

        <TabsContent value="groups" className="space-y-6">
          <GroupTemplatesTab />
        </TabsContent>

        <TabsContent value="conditions" className="space-y-6">
          <GeneralConditionsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
